import { BusinessOpeningHour } from '@/types/index';

// Days of the week mapping from Google Places API to our format
const DAYS_MAPPING = {
  0: 'Sunday',
  1: 'Monday', 
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
} as const;

// Parse weekday text format from new Google Places API
function parseWeekdayText(weekdayText: string[]): Omit<BusinessOpeningHour, 'id' | 'business_id'>[] {
  
  const result: Omit<BusinessOpeningHour, 'id' | 'business_id'>[] = [];
  
  // Day name mapping for parsing (supports multiple languages)
  const dayNameMapping: Record<string, string> = {
    // English
    'monday': 'Monday',
    'tuesday': 'Tuesday', 
    'wednesday': 'Wednesday',
    'thursday': 'Thursday',
    'friday': 'Friday',
    'saturday': 'Saturday',
    'sunday': 'Sunday',
    // Slovak
    'pondelok': 'Monday',
    'utorok': 'Tuesday',
    'streda': 'Wednesday',
    'štvrtok': 'Thursday',
    'piatok': 'Friday',
    'sobota': 'Saturday',
    'nedeľa': 'Sunday'
  };
  
  weekdayText.forEach((dayText, index) => {
    
    // Extract day name from the text (support Unicode characters for Slovak)
    const dayMatch = dayText.match(/^([^\s:]+):/);
    if (!dayMatch) return;
    
    const dayName = dayNameMapping[dayMatch[1].toLowerCase()];
    if (!dayName) return;
    
    // Check if closed (English and Slovak)
    if (dayText.toLowerCase().includes('closed') || 
        dayText.toLowerCase().includes('zatvorené') || 
        dayText.toLowerCase().includes('zavrete')) {
      result.push({
        day_of_week: dayName,
        opening_time: null,
        closing_time: null,
        is_closed_all_day: true,
        is_open: false,
      });
      return;
    }
    
    // Check if 24 hours (various formats in English and Slovak)
    if (dayText.match(/24\s*(hours?|hrs?|hodín)|open\s*24|24\/7|nonstop|nepretržite/i)) {
      result.push({
        day_of_week: dayName,
        opening_time: null,
        closing_time: null,
        is_closed_all_day: false,
        is_24_hours: true,
        is_open: true,
      });
      return;
    }
    
    // Parse time ranges - handle both em dash and regular dash, support 24-hour format
    const timeMatch = dayText.match(/(\d{1,2}:\d{2})\s*(AM|PM)?\s*[-–—]\s*(\d{1,2}:\d{2})\s*(AM|PM)?/);
    let openTime: string, closeTime: string, openPeriod: string | undefined, closePeriod: string | undefined;
    
    if (timeMatch) {
      [, openTime, openPeriod, closeTime, closePeriod] = timeMatch;
    } else {
      // Try Slovak format with dots (e.g., "8.00–20.00")
      const slovakMatch = dayText.match(/(\d{1,2})\.(\d{2})\s*[-–—]\s*(\d{1,2})\.(\d{2})/);
      if (slovakMatch) {
        const [, openHour, openMin, closeHour, closeMin] = slovakMatch;
        openTime = `${openHour}:${openMin}`;
        closeTime = `${closeHour}:${closeMin}`;
        openPeriod = undefined;
        closePeriod = undefined;
      } else {
        // Try 24-hour format without periods
        const hourMatch = dayText.match(/(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})/);
        if (hourMatch) {
          const [, openHour, openMin, closeHour, closeMin] = hourMatch;
          openTime = `${openHour}:${openMin}`;
          closeTime = `${closeHour}:${closeMin}`;
          openPeriod = undefined;
          closePeriod = undefined;
        } else {
          // No valid time format found
          result.push({
            day_of_week: dayName,
            opening_time: null,
            closing_time: null,
            is_closed_all_day: true,
            is_open: false,
          });
          return;
        }
      }
    }
    
    if (openTime && closeTime) {
      
      // For 24-hour format (no AM/PM), use the times directly
      if (!openPeriod && !closePeriod) {
        const openHour = parseInt(openTime.split(':')[0]);
        const closeHour = parseInt(closeTime.split(':')[0]);
        
        // Handle midnight (0:00) as next day closing time (23:59)
        let closeTime24 = closeTime;
        if (closeHour === 0 && openHour > 0) {
          closeTime24 = '23:59';
        }
        
        
        result.push({
          day_of_week: dayName,
          opening_time: openTime,
          closing_time: closeTime24,
          is_closed_all_day: false,
          is_open: true,
        });
      } else {
        // Handle AM/PM format
        let smartOpenPeriod = openPeriod;
        if (!openPeriod && closePeriod === 'PM') {
          // If opening time is in afternoon hours (12-23) without AM/PM, assume PM
          const openHour = parseInt(openTime.split(':')[0]);
          if (openHour >= 2 && openHour <= 11) {
            smartOpenPeriod = 'PM';
          } else {
            smartOpenPeriod = 'AM';
          }
        } else if (!openPeriod) {
          smartOpenPeriod = 'AM';
        }
        
        // Convert to 24-hour format
        const openTime24 = convertTo24Hour(openTime, smartOpenPeriod);
        const closeTime24 = convertTo24Hour(closeTime, closePeriod || 'PM');
        
        
        result.push({
          day_of_week: dayName,
          opening_time: openTime24,
          closing_time: closeTime24,
          is_closed_all_day: false,
          is_open: true,
        });
      }
    } else {
      // If we can't parse the time, mark as closed
      result.push({
        day_of_week: dayName,
        opening_time: null,
        closing_time: null,
        is_closed_all_day: true,
        is_open: false,
      });
    }
  });
  
  // Ensure we have all 7 days
  Object.values(DAYS_MAPPING).forEach(day => {
    if (!result.find(r => r.day_of_week === day)) {
      result.push({
        day_of_week: day,
        opening_time: null,
        closing_time: null,
        is_closed_all_day: true,
      });
    }
  });
  
  return result;
}

// Convert 12-hour time to 24-hour format
function convertTo24Hour(time: string, period: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  let hour24 = hours;
  
  if (period.toUpperCase() === 'PM' && hours !== 12) {
    hour24 = hours + 12;
  } else if (period.toUpperCase() === 'AM' && hours === 12) {
    hour24 = 0;
  }
  
  return `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Convert Google Places opening hours to our database format
export function convertGoogleOpeningHours(
  googleOpeningHours: google.maps.places.PlaceOpeningHours | undefined
): Omit<BusinessOpeningHour, 'id' | 'business_id'>[] {

  if (!googleOpeningHours) {
    // Return default closed hours for all days
    return Object.values(DAYS_MAPPING).map(day => ({
      day_of_week: day,
      opening_time: null,
      closing_time: null,
      is_closed_all_day: true,
      is_open: false,
    }));
  }

  // Check if we have weekday_text (new API format)
  if ((googleOpeningHours as any).weekday_text && Array.isArray((googleOpeningHours as any).weekday_text)) {
    return parseWeekdayText((googleOpeningHours as any).weekday_text);
  }

  // Fallback to periods format (old API)
  if (!googleOpeningHours.periods) {
    return Object.values(DAYS_MAPPING).map(day => ({
      day_of_week: day,
      opening_time: null,
      closing_time: null,
      is_closed_all_day: true,
      is_open: false,
    }));
  }

  // Initialize all days as closed
  const openingHours: Record<string, Omit<BusinessOpeningHour, 'id' | 'business_id'>> = {};
  Object.values(DAYS_MAPPING).forEach(day => {
    openingHours[day] = {
      day_of_week: day,
      opening_time: null,
      closing_time: null,
      is_closed_all_day: true,
      is_open: false,
    };
  });

  // Process Google Places periods (only if they have time data)
  googleOpeningHours.periods.forEach(period => {
    if (period.open && period.open.time) {
      const dayName = DAYS_MAPPING[period.open.day as keyof typeof DAYS_MAPPING];
      if (dayName) {
        const openTime = formatGoogleTime(period.open.time);
        let closeTime: string | null = null;
        
        if (period.close && period.close.time) {
          closeTime = formatGoogleTime(period.close.time);
        } else {
          // If no close time, it's a 24-hour business
          // Mark as 24 hours instead of setting arbitrary close time
          openingHours[dayName] = {
            day_of_week: dayName,
            opening_time: null,
            closing_time: null,
            is_closed_all_day: false,
            is_24_hours: true,
            is_open: true,
          };
          return;
        }

        openingHours[dayName] = {
          day_of_week: dayName,
          opening_time: openTime,
          closing_time: closeTime,
          is_closed_all_day: false,
          is_open: true,
        };
      }
    }
  });

  const result = Object.values(openingHours);
  return result;
}

// Convert Google time format (e.g., "0900", "1430") to HH:MM format
function formatGoogleTime(googleTime: string): string {
  if (!googleTime || googleTime.length !== 4) {
    return '00:00';
  }
  
  const hours = googleTime.substring(0, 2);
  const minutes = googleTime.substring(2, 4);
  return `${hours}:${minutes}`;
}

// Business type categories based on Google Places types
export enum BusinessCategory {
  RESTAURANT = 'restaurant',
  RETAIL = 'retail',
  SERVICE = 'service',
  HEALTH = 'health',
  BEAUTY = 'beauty',
  PROFESSIONAL = 'professional',
  HOSPITALITY = 'hospitality',
  ENTERTAINMENT = 'entertainment',
  OTHER = 'other'
}

// Business type detection configuration
export const BUSINESS_TYPE_MAPPING = {
  [BusinessCategory.RESTAURANT]: [
    'restaurant', 'cafe', 'bar', 'bakery', 'meal_delivery', 'meal_takeaway',
    'food', 'night_club', 'fast_food_restaurant', 'pizza_restaurant',
    'mexican_restaurant', 'chinese_restaurant', 'japanese_restaurant',
    'italian_restaurant', 'indian_restaurant', 'seafood_restaurant',
    'steak_house', 'vegan_restaurant', 'vegetarian_restaurant'
  ],
  [BusinessCategory.RETAIL]: [
    'store', 'shopping_mall', 'clothing_store', 'electronics_store',
    'furniture_store', 'hardware_store', 'home_goods_store',
    'jewelry_store', 'shoe_store', 'sporting_goods_store',
    'supermarket', 'grocery_store', 'convenience_store',
    'book_store', 'pet_store', 'florist', 'bicycle_store'
  ],
  [BusinessCategory.SERVICE]: [
    'car_repair', 'gas_station', 'laundry', 'electrician', 'plumber',
    'locksmith', 'moving_company', 'storage', 'car_wash', 'veterinary_care'
  ],
  [BusinessCategory.HEALTH]: [
    'hospital', 'doctor', 'dentist', 'pharmacy', 'physiotherapist',
    'veterinary_care', 'medical_lab', 'health'
  ],
  [BusinessCategory.BEAUTY]: [
    'beauty_salon', 'hair_care', 'spa', 'nail_salon', 'barber_shop'
  ],
  [BusinessCategory.PROFESSIONAL]: [
    'real_estate_agency', 'insurance_agency', 'travel_agency',
    'accounting', 'lawyer', 'finance'
  ],
  [BusinessCategory.HOSPITALITY]: [
    'lodging', 'hotel', 'motel', 'resort_hotel', 'bed_and_breakfast'
  ],
  [BusinessCategory.ENTERTAINMENT]: [
    'gym', 'tourist_attraction', 'amusement_park', 'aquarium',
    'art_gallery', 'museum', 'zoo', 'movie_theater', 'casino'
  ]
};

// Extract business information from Google Places result
export interface ExtractedBusinessInfo {
  name: string;
  address: string;
  phone?: string;
  website?: string;
  placeId: string;
  openingHours: Omit<BusinessOpeningHour, 'id' | 'business_id'>[];
  businessCategory: BusinessCategory;
  businessTypes: string[];
  country_code?: string;
}

export function extractBusinessInfoFromPlace(
  place: google.maps.places.PlaceResult
): ExtractedBusinessInfo {
  
  let openingHours = convertGoogleOpeningHours(place.opening_hours);
  
  // Enhanced 24-hour detection for common 24-hour businesses
  if (!place.opening_hours && place.name) {
    const businessName = place.name.toLowerCase();
    const is24HourBusiness = 
      businessName.includes('mcdonald') ||
      businessName.includes('burger king') ||
      businessName.includes('taco bell') ||
      businessName.includes('7-eleven') ||
      businessName.includes('circle k') ||
      businessName.includes('wawa') ||
      businessName.includes('sheetz') ||
      businessName.includes('gas station') ||
      businessName.includes('convenience store') ||
      businessName.includes('24') ||
      businessName.includes('always open');
    
    if (is24HourBusiness) {
      // Create 24-hour opening hours for all days
      openingHours = Object.values(DAYS_MAPPING).map(day => ({
        day_of_week: day,
        opening_time: null,
        closing_time: null,
        is_closed_all_day: false,
        is_24_hours: true,
        is_open: true,
      }));
    }
  }
  
  const businessTypes = place.types || [];
  const businessCategory = detectBusinessCategory(businessTypes);
  
  // Extract country code from address_components
  let country_code: string | undefined;
  if (place.address_components) {
    const countryComponent = place.address_components.find(
      component => component.types.includes('country')
    );
    if (countryComponent) {
      country_code = countryComponent.short_name; // This gives us ISO 3166-1 alpha-2 code (e.g., 'US', 'SK', 'DE')
    }
  }
  
  return {
    name: place.name || '',
    address: place.formatted_address || '',
    phone: place.formatted_phone_number || undefined,
    website: place.website || undefined,
    placeId: place.place_id || '',
    openingHours: openingHours,
    businessCategory: businessCategory,
    businessTypes: businessTypes,
    country_code: country_code,
  };
}

// Validate if a place is a business establishment
export function isValidBusinessPlace(place: google.maps.places.PlaceResult): boolean {
  if (!place.place_id || !place.name || !place.formatted_address) {
    return false;
  }

  // Check if it's a business establishment
  if (place.business_status === 'CLOSED_PERMANENTLY') {
    return false;
  }

  // Check if it has business-related types
  const businessTypes = [
    'establishment',
    'store',
    'restaurant',
    'food',
    'lodging',
    'health',
    'finance',
    'beauty_salon',
    'car_repair',
    'gas_station',
    'shopping_mall',
    'clothing_store',
    'electronics_store',
    'furniture_store',
    'hardware_store',
    'home_goods_store',
    'jewelry_store',
    'shoe_store',
    'sporting_goods_store',
    'supermarket',
    'pharmacy',
    'bakery',
    'cafe',
    'meal_delivery',
    'meal_takeaway',
    'night_club',
    'bar',
    'gym',
    'spa',
    'tourist_attraction',
    'amusement_park',
    'aquarium',
    'art_gallery',
    'museum',
    'zoo',
  ];

  return place.types?.some(type => businessTypes.includes(type)) || false;
}

// Determine business category from Google Places types
export function detectBusinessCategory(placeTypes: string[]): BusinessCategory {
  if (!placeTypes || placeTypes.length === 0) {
    return BusinessCategory.OTHER;
  }

  // Check each category in order of specificity
  for (const [category, types] of Object.entries(BUSINESS_TYPE_MAPPING)) {
    const hasType = placeTypes.some(placeType => 
      types.includes(placeType.toLowerCase())
    );
    if (hasType) {
      return category as BusinessCategory;
    }
  }

  return BusinessCategory.OTHER;
}

// Get business-specific feature recommendations
export function getBusinessFeatureRecommendations(category: BusinessCategory) {
  const recommendations = {
    [BusinessCategory.RESTAURANT]: {
      essential: ['Google Reviews', 'Loyalty Program', 'QR Menu'],
      recommended: ['Shift Management', 'Delivery Integration', 'Table Reservations'],
      optional: ['Inventory Tracking', 'Social Media Integration']
    },
    [BusinessCategory.RETAIL]: {
      essential: ['Loyalty Program', 'Product Reviews', 'Inventory Management'],
      recommended: ['Promotional Campaigns', 'Customer Analytics', 'Email Marketing'],
      optional: ['Appointment Booking', 'Service Reviews']
    },
    [BusinessCategory.SERVICE]: {
      essential: ['Appointment Scheduling', 'Service Reviews', 'Customer Communication'],
      recommended: ['Employee Time Tracking', 'Service Packages', 'Follow-up System'],
      optional: ['Loyalty Program', 'Product Sales']
    },
    [BusinessCategory.HEALTH]: {
      essential: ['Appointment Scheduling', 'Patient Reviews', 'Privacy Compliance'],
      recommended: ['Insurance Integration', 'Medical Records', 'Reminder System'],
      optional: ['Loyalty Program', 'Health Tips Newsletter']
    },
    [BusinessCategory.BEAUTY]: {
      essential: ['Appointment Scheduling', 'Service Reviews', 'Before/After Photos'],
      recommended: ['Loyalty Program', 'Package Deals', 'Staff Management'],
      optional: ['Product Sales', 'Social Media Integration']
    },
    [BusinessCategory.PROFESSIONAL]: {
      essential: ['Appointment Scheduling', 'Client Reviews', 'Document Management'],
      recommended: ['Consultation Tracking', 'Follow-up System', 'Referral Program'],
      optional: ['Billing Integration', 'Client Portal']
    },
    [BusinessCategory.HOSPITALITY]: {
      essential: ['Booking System', 'Guest Reviews', 'Room Management'],
      recommended: ['Loyalty Program', 'Concierge Services', 'Local Recommendations'],
      optional: ['Event Planning', 'Restaurant Integration']
    },
    [BusinessCategory.ENTERTAINMENT]: {
      essential: ['Event Management', 'Ticket Sales', 'Customer Reviews'],
      recommended: ['Membership Programs', 'Social Media Integration', 'Photo Sharing'],
      optional: ['Food & Beverage', 'Merchandise Sales']
    },
    [BusinessCategory.OTHER]: {
      essential: ['Customer Reviews', 'Basic Analytics', 'Contact Management'],
      recommended: ['Loyalty Program', 'Email Marketing', 'Social Media'],
      optional: ['Appointment Scheduling', 'Inventory Management']
    }
  };

  return recommendations[category] || recommendations[BusinessCategory.OTHER];
}